/**
 * VerticalVideoCarousel Component
 * 
 * Optimized for mobile performance with:
 * - Lazy loading using Intersection Observer
 * - Loading spinners and error handling
 * - Connection-aware preload strategies
 * - Efficient video management for different screen sizes
 * - Minimal data usage on slow connections
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';

const videos = [
  'https://ucarecdn.com/75dfe205-dfa8-4eea-ae54-a75cfd06e782/carousel_1.mp4',
  'videos/carousel_2.mp4',
  'videos/carousel_3.mp4',
  'videos/carousel_4.mp4',
];

// Test video URL accessibility
const testVideoUrl = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    console.log(`Video URL test for ${url}:`, response.ok ? 'SUCCESS' : 'FAILED', response.status);
    return response.ok;
  } catch (error) {
    console.error(`Failed to test video URL: ${url}`, error);
    return false;
  }
};

interface VideoLoadingState {
  [key: number]: {
    loading: boolean;
    loaded: boolean;
    error: boolean;
  };
}

export default function VerticalVideoCarousel() {
  const [current, setCurrent] = useState(0);
  const [videoLoadingStates, setVideoLoadingStates] = useState<VideoLoadingState>({});
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [isSlowConnection, setIsSlowConnection] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const mobileVideoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Initialize loading states
  useEffect(() => {
    const initialStates: VideoLoadingState = {};
    videos.forEach((_, idx) => {
      initialStates[idx] = { loading: false, loaded: false, error: false };
    });
    setVideoLoadingStates(initialStates);
  }, []);

  // Test video URLs accessibility
  useEffect(() => {
    const testAllVideos = async () => {
      console.log('Testing video URLs accessibility...');
      for (let i = 0; i < videos.length; i++) {
        await testVideoUrl(videos[i]);
      }
    };
    testAllVideos();
  }, []);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      {
        threshold: 0.1,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  // Detect slow connections for further optimization
  useEffect(() => {
    // Check for slow connection
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        const slowConnections = ['slow-2g', '2g', '3g'];
        setIsSlowConnection(
          slowConnections.includes(connection.effectiveType) ||
          connection.downlink < 1.5
        );
      }
    }
    
    // Fallback: detect mobile devices as potentially slower
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile && !('connection' in navigator)) {
      setIsSlowConnection(true);
    }
  }, []);

  // Handle video loading states
  const handleVideoLoadStart = useCallback((idx: number) => {
    setVideoLoadingStates(prev => ({
      ...prev,
      [idx]: { ...prev[idx], loading: true, error: false }
    }));
  }, []);

  const handleVideoCanPlay = useCallback((idx: number) => {
    setVideoLoadingStates(prev => ({
      ...prev,
      [idx]: { ...prev[idx], loading: false, loaded: true, error: false }
    }));
  }, []);
  const handleVideoError = useCallback((idx: number, event?: Event) => {
    console.error(`Video ${idx} failed to load:`, videos[idx]);
    if (event && event.target) {
      const video = event.target as HTMLVideoElement;
      console.error('Video error details:', {
        error: video.error,
        networkState: video.networkState,
        readyState: video.readyState,
        src: video.src
      });
    }
    
    setVideoLoadingStates(prev => ({
      ...prev,
      [idx]: { ...prev[idx], loading: false, loaded: false, error: true }
    }));
  }, []);
  const goTo = (idx: number) => {
    setCurrent(idx);
    
    // Optimize preloading based on connection speed
    if (!isSlowConnection) {
      // Preload current and adjacent videos only on fast connections
      const videosToPreload = [
        idx,
        (idx + 1) % videos.length,
        (idx - 1 + videos.length) % videos.length
      ];
      
      videosToPreload.forEach(videoIdx => {
        const video = videoRefs.current[videoIdx] || (videoIdx === idx ? mobileVideoRef.current : null);
        if (video && video.preload !== 'auto') {
          video.preload = 'auto';
          video.load(); // Force load for better UX
        }
      });
    } else {
      // On slow connections, only preload current video
      const currentVideo = videoRefs.current[idx] || mobileVideoRef.current;
      if (currentVideo) {
        currentVideo.preload = 'metadata';
        currentVideo.load();
      }
    }

    // Handle mobile video ref
    if (mobileVideoRef.current) {
      mobileVideoRef.current.play().catch(() => {
        // Handle autoplay restrictions
      });
    }
    // Handle desktop video refs
    if (videoRefs.current[idx]) {
      videoRefs.current[idx]?.play().catch(() => {
        // Handle autoplay restrictions
      });
    }
  };

  const next = () => goTo((current + 1) % videos.length);
  const prev = () => goTo((current - 1 + videos.length) % videos.length);
  // Ensure only the current video is playing
  useEffect(() => {
    if (!isIntersecting) return;

    // Handle mobile video
    if (mobileVideoRef.current) {
      mobileVideoRef.current.play().catch(() => {
        // Handle autoplay restrictions
      });
    }
    
    // Handle desktop videos - pause all except current
    videoRefs.current.forEach((video, idx) => {
      if (video) {
        if (idx === current) {
          video.play().catch(() => {
            // Handle autoplay restrictions
          });
        } else {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  }, [current, isIntersecting]);

  const LoadingSpinner = () => (
    <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
    </div>
  );
  const ErrorMessage = ({ videoIndex }: { videoIndex: number }) => (
    <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="text-white text-center p-4">
        <svg className="w-8 h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        <p className="text-sm mb-2">Video failed to load</p>
        <button 
          onClick={() => {
            // Retry loading the video
            const video = videoRefs.current[videoIndex] || (videoIndex === current ? mobileVideoRef.current : null);
            if (video) {
              video.load();
            }
          }}
          className="text-xs bg-white/20 px-3 py-1 rounded hover:bg-white/30 transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="w-full flex flex-col items-center py-16 bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      <h2 className="text-3xl font-extrabold text-neutral-900 mb-2 text-center">Bethanchok in Motion</h2>
      <p className="text-lg text-neutral-600 mb-8 text-center max-w-2xl">
        Experience the sights and sounds of Bethanchok village. Explore our homestay, the vibrant local life, and breathtaking Himalayan views through these short vertical videos.
      </p>
      
      {isIntersecting && (
        <div className="relative w-full flex justify-center items-center">
        {/* Navigation Arrows - positioned for both mobile and desktop */}
        <button 
          onClick={prev} 
          className="absolute left-2 md:left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-primary-600 hover:text-white text-primary-700 rounded-full p-2 md:p-3 shadow-lg transition-all duration-200 focus:outline-none"
        >
          <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor' className='w-5 h-5 md:w-6 md:h-6'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
          </svg>
        </button>        {/* Mobile: Single video display */}
        <div className="block md:hidden w-full max-w-[280px] mx-auto">
          <div className="aspect-[9/16] w-full rounded-2xl overflow-hidden shadow-2xl bg-black relative">            <video
              ref={mobileVideoRef}
              src={videos[current]}
              className="w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
              preload={isSlowConnection ? "none" : "metadata"}
              style={{ aspectRatio: '9/16' }}
              onLoadStart={() => handleVideoLoadStart(current)}
              onCanPlay={() => handleVideoCanPlay(current)}
              onError={(e) => handleVideoError(current, e.nativeEvent)}
              crossOrigin="anonymous"
            />
            {videoLoadingStates[current]?.loading && <LoadingSpinner />}
            {videoLoadingStates[current]?.error && <ErrorMessage videoIndex={current} />}
          </div>
        </div>{/* Desktop/Tablet: Three video display */}
        <div className="hidden md:flex flex-row gap-4 w-full max-w-[1200px] justify-center items-center">
          {[ -1, 0, 1 ].map(offset => {
            const idx = (current + offset + videos.length) % videos.length;
            const isCenter = offset === 0;
            return (              <div key={idx} className={`aspect-[9/16] w-[180px] lg:w-[240px] xl:w-[320px] rounded-2xl overflow-hidden shadow-2xl bg-black flex items-center justify-center transition-all duration-500 relative ${isCenter ? 'scale-105 z-10' : 'opacity-80 scale-95 z-0'}`}>                <video
                  ref={el => { videoRefs.current[idx] = el; }}
                  src={videos[idx]}
                  className="w-full h-full object-cover"
                  autoPlay={isCenter}
                  loop
                  muted
                  playsInline
                  preload={isSlowConnection ? (isCenter ? "metadata" : "none") : (isCenter ? "auto" : "metadata")}
                  style={{ aspectRatio: '9/16' }}
                  onLoadStart={() => handleVideoLoadStart(idx)}
                  onCanPlay={() => handleVideoCanPlay(idx)}
                  onError={(e) => handleVideoError(idx, e.nativeEvent)}
                  crossOrigin="anonymous"
                />
                {videoLoadingStates[idx]?.loading && <LoadingSpinner />}
                {videoLoadingStates[idx]?.error && <ErrorMessage videoIndex={idx} />}
              </div>
            );
          })}
        </div>
        
        <button 
          onClick={next} 
          className="absolute right-2 md:right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-primary-600 hover:text-white text-primary-700 rounded-full p-2 md:p-3 shadow-lg transition-all duration-200 focus:outline-none"
        >
          <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor' className='w-5 h-5 md:w-6 md:h-6'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
          </svg>        </button>
        </div>
      )}
      
      <div className="flex gap-2 mt-6 justify-center">
        {videos.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`w-3 h-3 rounded-full ${current === idx ? 'bg-primary-600' : 'bg-neutral-300'} transition-all`}
            aria-label={`Go to video ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
